(ns hai.web-app.sync
  "Keeps every open tab current. A save tells the other tabs which scope
  changed; a tab that hears it refetches what it shows of that scope."
  (:require
   [goog.object :as gobj]
   [hai.ui.rf-sugar :refer [>e defeffect defevent defevent-fx]]))

(defonce ^:private channel (atom nil))

(defn- refetches
  [db scope]
  (into []
        (comp (keep #(get % scope))
              cat
              (distinct))
        (vals (::watchers db))))

;; What an owner — a page, the home cards — refetches when another tab changes
;; `scope`. Owners unwatch on the way out.
(defevent ::watch
  [db {:keys [owner scope refetch]}]
  (assoc-in db [::watchers owner scope] refetch))

(defevent ::unwatch
  [db {:keys [owner]}]
  (update db ::watchers dissoc owner))

(defevent-fx ::refetch
  [{:keys [db]} {:keys [scopes]}]
  {:fx (into []
             (comp (mapcat #(refetches db %))
                   (distinct)
                   (map (fn [event] [:dispatch event])))
             scopes)})

;; A hidden tab keeps the scopes it missed and refetches each once, when shown.
(defevent ::missed
  [db {:keys [scope]}]
  (update db ::missed-scopes (fnil conj #{}) scope))

(defevent-fx ::shown
  [{:keys [db]} _]
  {:db (dissoc db ::missed-scopes)
   :fx [[:dispatch [::refetch {:scopes (::missed-scopes db)}]]]})

(defn- heard!
  [scope]
  (if (.-hidden js/document)
    (>e [::missed {:scope scope}])
    (>e [::refetch {:scopes [scope]}])))

(defn- on-channel-message
  [^js event]
  (heard! (keyword (gobj/get (.-data event) "scope"))))

;; The browser extension's content script relays a sidebar's change to the
;; page. This page's own message, meant for the extension, is ignored.
(defn- on-window-message
  [^js event]
  (let [data (.-data event)]
    (when (and (identical? (.-source event) js/window)
               (= (.-origin event) js/location.origin)
               (object? data)
               (= "sync-changed" (gobj/get data "humble_ext_event"))
               (= "extension" (gobj/get data "source")))
      (heard! (keyword (gobj/get data "scope"))))))

(defn- on-visibility-change
  []
  (when-not (.-hidden js/document)
    (>e [::shown])))

;; The saving tab already shows the change; a channel never hears its own
;; messages, so only the other tabs refetch. The page message reaches the
;; browser extension's content script, when installed, for its sidebars.
(defn- post-to-tabs!
  [scope]
  (some-> @channel (.postMessage #js {"scope" (name scope)})))

(defeffect ::post
  [{:keys [scope]}]
  (post-to-tabs! scope)
  (js/window.postMessage #js {"humble_ext_event" "sync-changed"
                              "scope" (name scope)
                              "source" "web-app"}
                         js/location.origin))

;; For a scope only web-app tabs watch: a sidebar refetches everything it shows
;; on a scope it does not know.
(defeffect ::post-to-tabs
  [{:keys [scope]}]
  (post-to-tabs! scope))

(defevent-fx ::tell-tabs
  [_ payload]
  {::post-to-tabs payload})

(defevent-fx :sync/changed
  [_ payload]
  {::post payload})

(defn init! []
  (when (exists? js/BroadcastChannel)
    (reset! channel (doto (js/BroadcastChannel. "humble-sync")
                      (.addEventListener "message" on-channel-message))))
  (js/window.addEventListener "message" on-window-message)
  (js/document.addEventListener "visibilitychange" on-visibility-change))
